import React, { useState, useEffect, useRef } from 'react';
import { X, Calendar, ChevronDown, MapPin } from 'lucide-react';

const BudgetEntryModal = ({ isOpen, onClose, chartOfAccounts = [], onSave, location }) => {
  // location object { id, name, ... } is passed as a prop
  // orgCode will be added by the parent component (Budgeting.js) during the save process
  
  const [formData, setFormData] = useState({
    // locationId is derived from the location prop but added by the parent on save
    budgetDate: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD
    budgetType: '',
    budgetCategory: '',
    budgetSubCategory: '',
    budgetDescription: '',
    budgetAmount: '',
    accountNumber: '', // Added accountNumber to store selected account number
    chartOfAccountId: null // Added chartOfAccountId to store selected account ID
  });

  const [accountSelected, setAccountSelected] = useState(false);
  const [errors, setErrors] = useState({});
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  
  // Ref for modal content 
  const modalRef = useRef(null);
  
  // Budget types
  const budgetTypes = ['Revenue', 'Expense', 'Capital'];
  
  // Current month and year for initial selection
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June', 
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Set default date to first day of current month
  useEffect(() => {
    // Create date for first day of current month in UTC
    const firstDayOfMonth = new Date(Date.UTC(currentYear, currentMonth, 1, 0, 0, 0));
    const formattedDate = firstDayOfMonth.toISOString().split('T')[0];
    
    setFormData(prev => ({
      ...prev,
      budgetDate: formattedDate
    }));
  }, []);
  
  // Set focus to first input when modal opens
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const firstInput = modalRef.current.querySelector('input');
      if (firstInput) {
        // Delay focus slightly to ensure modal transition is complete
        setTimeout(() => firstInput.focus(), 100); 
      }
    }
  }, [isOpen]);

  // Load preselected account if provided
  useEffect(() => {
    if (chartOfAccounts && chartOfAccounts.length > 0) {
      // Can pre-populate the form with the first account if needed
    }
  }, [chartOfAccounts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleAccountSelect = (accountId) => {
    if (!accountId) {
       setAccountSelected(false);
       // Optionally clear related fields if needed
       // setFormData(prev => ({ ...prev, budgetCategory: '', budgetDescription: '', budgetType: '', accountNumber: '' }));
       return;
    }
    
    const selectedAccount = chartOfAccounts.find(account => account.id === parseInt(accountId, 10));
    
    if (selectedAccount) {
      setFormData(prev => ({
        ...prev,
        budgetCategory: selectedAccount.accountNumber,
        budgetDescription: selectedAccount.accountName,
        budgetType: selectedAccount.accountType || 'Expense', // Default to Expense if not specified
        accountNumber: selectedAccount.accountNumber, // Store the account number
        chartOfAccountId: selectedAccount.id // Store the chartOfAccountId
      }));
      
      setAccountSelected(true);
      // Clear errors for account-related fields
      setErrors(prev => ({ ...prev, budgetCategory: null, budgetDescription: null, budgetType: null }));
    }
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    
    setFormData(prev => ({
      ...prev,
      budgetAmount: value
    }));
    
    if (errors.budgetAmount) {
      setErrors(prev => ({
        ...prev,
        budgetAmount: null
      }));
    }
  };
  
  // Handle click outside of dropdowns to close them
  // This specific handler seems unused, can be removed if not needed elsewhere
  // const handleClickOutside = (e, dropdownSetter) => {
  //   if (e.target.closest('.dropdown-container')) return;
  //   dropdownSetter(false);
  // };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.budgetDate) {
      newErrors.budgetDate = 'Date is required';
    }
    
    // Validate based on selected account or manually entered category/description
    if (!formData.accountNumber && !formData.budgetCategory) {
      newErrors.budgetCategory = 'Account selection or Category is required';
    } 

    if (!formData.budgetDescription) {
      newErrors.budgetDescription = 'Description is required';
    }
    
    if (!formData.budgetAmount) {
      newErrors.budgetAmount = 'Amount is required';
    } else if (isNaN(parseFloat(formData.budgetAmount))) {
      newErrors.budgetAmount = 'Amount must be a valid number';
    }
    
    if (!formData.budgetType) {
      newErrors.budgetType = 'Budget type is required';
    }
    
    // Ensure we have either a valid chartOfAccountId or accountNumber
    if (!formData.chartOfAccountId && !formData.accountNumber) {
      newErrors.budgetCategory = newErrors.budgetCategory || 'A valid account must be selected';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Ensure the date is set to the 1st of the month
      const dateObj = new Date(formData.budgetDate);
      const year = dateObj.getFullYear();
      const month = dateObj.getMonth();
      
      // Create a new UTC date for the 1st of the month
      const firstOfMonth = new Date(Date.UTC(year, month, 1, 0, 0, 0));
      
      // Find chartOfAccountId if we have an accountNumber but no chartOfAccountId
      let chartOfAccountId = formData.chartOfAccountId;
      
      // Debug log to see what we have before processing

      // Enhanced and more robust chartOfAccountId and accountNumber lookup
      if (!chartOfAccountId) {
        // First try to find by account number
        if (formData.accountNumber) {
          const matchingAccount = chartOfAccounts.find(acc => acc.accountNumber === formData.accountNumber);
          if (matchingAccount) {
            chartOfAccountId = matchingAccount.id;

          }
        }
        
        // If that fails, try to find by category (which might contain the account number)
        if (!chartOfAccountId && formData.budgetCategory) {
          const matchingAccount = chartOfAccounts.find(acc => acc.accountNumber === formData.budgetCategory);
          if (matchingAccount) {
            chartOfAccountId = matchingAccount.id;

          }
        }
        
        // If we still don't have a chartOfAccountId but have an account selection, try other matching methods
        if (!chartOfAccountId && chartOfAccounts.length > 0) {
          // Try partial matching by description if we have one
          if (formData.budgetDescription) {
            const matchByDescription = chartOfAccounts.find(acc => 
              acc.accountName && formData.budgetDescription &&
              acc.accountName.toLowerCase() === formData.budgetDescription.toLowerCase());
            
            if (matchByDescription) {
              chartOfAccountId = matchByDescription.id;
              formData.accountNumber = matchByDescription.accountNumber;

            }
          }
        }
      }
      
      // If we have a chartOfAccountId but no accountNumber, get the accountNumber from the chart of accounts
      if (chartOfAccountId && !formData.accountNumber) {
        const account = chartOfAccounts.find(acc => acc.id === chartOfAccountId);
        if (account) {
          formData.accountNumber = account.accountNumber;

        }
      }
      
      // Convert amount to a number for submission
      const submissionData = {
        ...formData,
        budgetDate: firstOfMonth.toISOString(), // Use ISO string with adjusted date
        budgetAmount: parseFloat(formData.budgetAmount), // Use budgetAmount key for API
        // Ensure accountNumber is included if it was set from dropdown
        accountNumber: formData.accountNumber || formData.budgetCategory, // Use category if no account number
        chartOfAccountId: chartOfAccountId, // Include chartOfAccountId
        
        // Ensure these fields are never null to avoid the error in BudgetTable
        budgetType: formData.budgetType || 'Expense', // Default to Expense if not provided
        budgetCategory: formData.budgetCategory || formData.accountNumber || '', // Use accountNumber as fallback
        budgetDescription: formData.budgetDescription || '' // Ensure not null
      };
      
      // Log the submission data for debugging

      // Additional debug for chartOfAccountId mapping
      if (chartOfAccountId) {

      } else {
        console.warn(`No chartOfAccountId found for account ${submissionData.accountNumber} - this could cause issues`);
      }
      // Parent (Budgeting.js) will add orgCode and locationId

      onSave(submissionData);
    }
  };
  
  // Close modal when Escape key is pressed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4" onClick={() => onClose()}>
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800">Create Budget Entry</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Location Display */}
            {location && (
              <div className="col-span-2 bg-gray-50 rounded-md p-3 mb-2 flex items-center">
                <MapPin size={18} className="text-orange-500 mr-2" />
                <span className="text-gray-700 font-medium">Location: {location.name}</span>
              </div>
            )}
            
            {/* Date Field */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  name="budgetDate"
                  value={formData.budgetDate}
                  onChange={handleInputChange}
                  className={`w-full p-2 border rounded-md ${errors.budgetDate ? 'border-red-500' : 'border-gray-300'}`}
                />
                <Calendar className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
              {errors.budgetDate && (
                <p className="mt-1 text-sm text-red-500">{errors.budgetDate}</p>
              )}
            </div>
            
            {/* Budget Type Dropdown */}
            <div className="col-span-1 dropdown-container">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Budget Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className={`w-full p-2 border rounded-md text-left flex justify-between items-center ${errors.budgetType ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <span>{formData.budgetType || 'Select Type'}</span>
                  <ChevronDown size={16} />
                </button>
                {showTypeDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                    {budgetTypes.map(type => (
                      <div
                        key={type}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, budgetType: type }));
                          setShowTypeDropdown(false);
                        }}
                      >
                        {type}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {errors.budgetType && (
                <p className="mt-1 text-sm text-red-500">{errors.budgetType}</p>
              )}
            </div>
            
            {/* Account Selection */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account <span className="text-red-500">*</span>
              </label>
              <select
                name="account"
                onChange={(e) => handleAccountSelect(e.target.value)}
                className={`w-full p-2 border rounded-md ${errors.budgetCategory ? 'border-red-500' : 'border-gray-300'}`}
                defaultValue=""
              >
                <option value="" disabled>Select an Account</option>
                {chartOfAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.accountNumber} - {account.accountName}
                  </option>
                ))}
              </select>
              {errors.budgetCategory && (
                <p className="mt-1 text-sm text-red-500">{errors.budgetCategory}</p>
              )}
            </div>
            
            {/* Description Field */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="budgetDescription"
                value={formData.budgetDescription}
                onChange={handleInputChange}
                placeholder="Enter budget description"
                className={`w-full p-2 border rounded-md ${errors.budgetDescription ? 'border-red-500' : 'border-gray-300'}`}
              />
              {errors.budgetDescription && (
                <p className="mt-1 text-sm text-red-500">{errors.budgetDescription}</p>
              )}
            </div>
            
            {/* Category & Sub-Category Fields */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="budgetCategory"
                value={formData.budgetCategory}
                onChange={handleInputChange}
                placeholder="Enter category"
                className={`w-full p-2 border rounded-md ${errors.budgetCategory ? 'border-red-500' : 'border-gray-300'}`}
              />
            </div>
            
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sub-Category
              </label>
              <input
                type="text"
                name="budgetSubCategory"
                value={formData.budgetSubCategory}
                onChange={handleInputChange}
                placeholder="Enter sub-category"
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            
            {/* Amount Field */}
            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2.5">$</span>
                <input
                  type="text"
                  name="budgetAmount"
                  value={formData.budgetAmount}
                  onChange={handleAmountChange}
                  placeholder="0.00"
                  className={`w-full p-2 pl-8 border rounded-md ${errors.budgetAmount ? 'border-red-500' : 'border-gray-300'}`}
                />
              </div>
              {errors.budgetAmount && (
                <p className="mt-1 text-sm text-red-500">{errors.budgetAmount}</p>
              )}
            </div>
          </div>
          
          <div className="mt-8 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 rounded-md text-sm font-medium text-white hover:bg-orange-600"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetEntryModal; 